import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import type {
  AsyncTaskRecord,
  ZEDrawingFeatureCollection,
  ZEMapAnnotation,
  ZERepeatStatus,
  ZETenderParcelDraft,
} from "./types";

const qk = {
  tenders: ["zt", "tenders"] as const,
  tender: (id: string) => ["zt", "tender", id] as const,
  elementTypes: ["zt", "element-types"] as const,
  variants: (t: string) => ["zt", "variants", t] as const,
  drawing: (v: string) => ["zt", "drawing", v] as const,
  summary: (v: string) => ["zt", "summary", v] as const,
  parcels: (v: string) => ["zt", "parcels", v] as const,
  parcelDrafts: (v: string) => ["zt", "parcel-drafts", v] as const,
  roadNeighbors: (v: string) => ["zt", "road-neighbors", v] as const,
  annotations: (v: string) => ["zt", "annotations", v] as const,
  clients: ["zt", "clients"] as const,
  task: (id: string) => ["bg-task", id] as const,
};

// ── tenders ──────────────────────────────────────────────────────────────────
export const useZETenders = () => useQuery({ queryKey: qk.tenders, queryFn: api.getZETenders });

export const useZETenderActionCounts = (enabled = true) =>
  useQuery({ queryKey: ["zt", "action-counts"], queryFn: api.getZETenderActionCounts, enabled });

export const useZETender = (id: string | undefined) =>
  useQuery({ queryKey: qk.tender(id ?? ""), queryFn: () => api.getZETender(id!), enabled: !!id });

export const useCreateZETender = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.createZETender>[0]) => api.createZETender(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tenders }),
  });
};

// ── element-type catalog ─────────────────────────────────────────────────────
export const useZEElementTypes = () =>
  useQuery({ queryKey: qk.elementTypes, queryFn: api.getZEElementTypes, staleTime: 5 * 60_000 });

// ── DXF export layer presets (for a re-layered download) ─────────────────────
export const useVariantLayerPresets = (variantId: string | undefined) =>
  useQuery({
    queryKey: ["zt", "layer-presets", variantId ?? ""],
    queryFn: () => api.getVariantLayerPresets(variantId!),
    enabled: !!variantId,
  });

// ── variants ─────────────────────────────────────────────────────────────────
export const useZEVariants = (zeTender: string | undefined) =>
  useQuery({
    queryKey: qk.variants(zeTender ?? ""),
    queryFn: () => api.getZEVariants(zeTender!),
    enabled: !!zeTender,
  });

export const useCreateZEVariant = (zeTender: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { label: string; note?: string }) => api.createZEVariant(zeTender, v.label, v.note),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variants(zeTender) }),
  });
};

export const usePatchZEVariant = (zeTender: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof api.patchZEVariant>[1] }) =>
      api.patchZEVariant(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variants(zeTender) }),
  });
};

export const useDeleteZEVariant = (zeTender: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteZEVariant(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variants(zeTender) }),
  });
};

export const useCopyZEVariant = (zeTender: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sourceId, label }: { sourceId: string; label: string }) => {
      const newVariant = await api.createZEVariant(zeTender, label);
      const drawing = await api.getVariantDrawing(sourceId);
      const hasDrawing = drawing.features.length > 0;
      if (hasDrawing) {
        await api.saveDrawing(newVariant.id, drawing);
      }
      // Carry over per-parcel drafts (ownership overrides incl. "działka końcowa" + WPW
      // counts). These hold the MANUAL parcel kinds that ULDK can't re-derive, so without
      // copying them a recompute would drop every hand-tagged type. Drafts join the
      // recomputed parcels by (variant, teryt); the route is identical so the teryts match.
      const drafts = await api.getZETenderParcelDrafts(sourceId);
      await Promise.all(
        drafts
          .filter((d) => d.ownership_override !== "" || d.wpw_count != null)
          .map((d) =>
            api.saveZETenderParcelDraft(newVariant.id, d.teryt, {
              ownership_override: d.ownership_override,
              wpw_count: d.wpw_count,
            }),
          ),
      );
      // Recompute parcels on the copy so every parcel kind (ULDK codes + the copied manual
      // overrides) is counted right after import — same async flow as the manual button.
      let recomputeTaskId: string | null = null;
      let mpzpTaskId: string | null = null;
      let protectedAreasTaskId: string | null = null;
      let waterCrossingsTaskId: string | null = null;
      if (hasDrawing) {
        const r = await api.recomputeParcels(newVariant.id);
        recomputeTaskId = r.task_id;
        mpzpTaskId = r.mpzp_task_id ?? null;
        protectedAreasTaskId = r.protected_areas_task_id ?? null;
        waterCrossingsTaskId = r.water_crossings_task_id ?? null;
      }
      return {
        ...newVariant,
        recomputeTaskId,
        mpzpTaskId,
        protectedAreasTaskId,
        waterCrossingsTaskId,
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variants(zeTender) }),
  });
};

// ── drawing ──────────────────────────────────────────────────────────────────
export const useVariantDrawing = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.drawing(variantId ?? ""),
    queryFn: () => api.getVariantDrawing(variantId!),
    enabled: !!variantId,
  });

export const useSaveDrawing = (variantId: string, zeTender: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fc: ZEDrawingFeatureCollection) => api.saveDrawing(variantId, fc),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.summary(variantId) });
      qc.invalidateQueries({ queryKey: qk.drawing(variantId) });
      qc.invalidateQueries({ queryKey: qk.variants(zeTender) });
    },
  });
};

export const useRecomputeParcels = (variantId: string) =>
  useMutation({ mutationFn: () => api.recomputeParcels(variantId) });

export const useQuantitySummary = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.summary(variantId ?? ""),
    queryFn: () => api.getQuantitySummary(variantId!),
    enabled: !!variantId,
  });

export const useZETenderParcels = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.parcels(variantId ?? ""),
    queryFn: () => api.getZETenderParcels(variantId!),
    enabled: !!variantId,
  });

// ── internal-road neighbours (działki ościenne dróg wewnętrznych) ─────────────
export const useZERoadNeighbors = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.roadNeighbors(variantId ?? ""),
    queryFn: () => api.getRoadNeighbors(variantId!),
    enabled: !!variantId,
  });

// Variant comes in per call, not per hook: the parcels panel targets the active variant
// while the pricing panel targets whichever variant the author picked for the import, and
// both must share one "obliczanie…" state.
export const useRecomputeRoadNeighbors = () =>
  useMutation({ mutationFn: (variantId: string) => api.recomputeRoadNeighbors(variantId) });

export const useClients = () => useQuery({ queryKey: qk.clients, queryFn: api.getClients });

// ── parcel drafts ─────────────────────────────────────────────────────────────
export const useZETenderParcelDrafts = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.parcelDrafts(variantId ?? ""),
    queryFn: () => api.getZETenderParcelDrafts(variantId!),
    enabled: !!variantId,
  });

export const useSaveZETenderParcelDraft = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teryt, data }: { teryt: string; data: { ownership_override?: string; wpw_count?: number | null } }) =>
      api.saveZETenderParcelDraft(variantId, teryt, data),
    onSuccess: (updated) => {
      qc.setQueryData<ZETenderParcelDraft[]>(qk.parcelDrafts(variantId), (prev) => {
        if (!prev) return [updated];
        const idx = prev.findIndex((d) => d.teryt === updated.teryt);
        if (idx >= 0) { const next = [...prev]; next[idx] = updated; return next; }
        return [...prev, updated];
      });
    },
  });
};

// ── map annotations ──────────────────────────────────────────────────────────
export const useZEMapAnnotations = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.annotations(variantId ?? ""),
    queryFn: () => api.getZEMapAnnotations(variantId!),
    enabled: !!variantId,
  });

export const useCreateZEMapAnnotation = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.createZEMapAnnotation>[0]) =>
      api.createZEMapAnnotation(payload),
    onSuccess: (created) => {
      qc.setQueryData<ZEMapAnnotation[]>(qk.annotations(variantId), (prev) =>
        prev ? [...prev, created] : [created],
      );
    },
  });
};

export const useUpdateZEMapAnnotation = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof api.updateZEMapAnnotation>[1] }) =>
      api.updateZEMapAnnotation(id, payload),
    onSuccess: (updated) => {
      qc.setQueryData<ZEMapAnnotation[]>(qk.annotations(variantId), (prev) => {
        if (!prev) return [updated];
        return prev.map((a) => (a.id === updated.id ? updated : a));
      });
    },
  });
};

export const useDeleteZEMapAnnotation = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteZEMapAnnotation(id),
    onSuccess: (_, id) => {
      qc.setQueryData<ZEMapAnnotation[]>(qk.annotations(variantId), (prev) =>
        prev ? prev.filter((a) => a.id !== id) : [],
      );
    },
  });
};

// ── async task cancel ────────────────────────────────────────────────────────
export const useCancelTask = (taskId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelTask(taskId!),
    onSuccess: (data) => {
      if (taskId) qc.setQueryData(qk.task(taskId), data);
    },
  });
};

// ── async task polling (recompute progress) ──────────────────────────────────
export const useTaskPolling = (taskId: string | null) =>
  useQuery<AsyncTaskRecord>({
    queryKey: qk.task(taskId ?? ""),
    queryFn: () => api.getTask(taskId!),
    enabled: !!taskId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && ["success", "failure", "cancelled"].includes(s) ? false : 1500;
    },
  });

// ── ZE Capabilities ──────────────────────────────────────────────────────────
const capQk = ["ze-capabilities"] as const;

export const useZECapabilities = () =>
  useQuery({ queryKey: capQk, queryFn: api.getZECapabilities });

export const useEnergaBranches = () =>
  useQuery({ queryKey: ["energa-branches"], queryFn: api.getEnergaBranches, staleTime: 300_000 });

export const usePatchZECapabilities = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.patchZECapabilities>[0]) =>
      api.patchZECapabilities(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: capQk }),
  });
};

// ── ZETender AI analysis + reanalyze ─────────────────────────────────────────
export const useReanalyzeZETender = (tenderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.reanalyzeZETender(tenderId),
    onSuccess: () => {
      // Refresh analysis after task kicks off (workspace polls the task separately)
      setTimeout(() => qc.invalidateQueries({ queryKey: [...qk.tender(tenderId), "ai-analysis"] }), 3000);
    },
  });
};

export const useZETenderAiAnalysis = (tenderId: string | undefined) =>
  useQuery({
    queryKey: [...qk.tender(tenderId ?? ""), "ai-analysis"] as const,
    queryFn: () => api.getZETenderAiAnalysis(tenderId!),
    enabled: !!tenderId,
    staleTime: 5 * 60_000,
    retry: false, // non-ZE tenders 404 here — no point retrying
  });

// ── EML import ───────────────────────────────────────────────────────────────
export const useStartEmlImport = () =>
  useMutation({ mutationFn: (file: File) => api.startEmlImport(file) });

export const useReanalyzeEmlRfx = () =>
  useMutation({
    mutationFn: ({ taskId, rfx }: { taskId: string; rfx: string }) =>
      api.reanalyzeEmlRfx(taskId, rfx),
  });

export const useConfirmEmlImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.confirmEmlImport>[0]) =>
      api.confirmEmlImport(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tenders }),
  });
};

// Invalidate parcels + summary once a recompute finishes (called from the workspace effect).
export const useInvalidateAfterRecompute = () => {
  const qc = useQueryClient();
  return (variantId: string, zeTender: string) => {
    qc.invalidateQueries({ queryKey: qk.parcels(variantId) });
    qc.invalidateQueries({ queryKey: qk.summary(variantId) });
    qc.invalidateQueries({ queryKey: qk.roadNeighbors(variantId) });
    qc.invalidateQueries({ queryKey: qk.variants(zeTender) });
  };
};

// ── powtórki anulowanych przetargów ──────────────────────────────────────────
export const useZERepeatMatches = (tenderId: string | undefined) =>
  useQuery({
    queryKey: ["zt", "repeat-matches", tenderId ?? ""],
    queryFn: () => api.getRepeatMatches(tenderId!),
    enabled: !!tenderId,
  });

export const useReviewRepeatMatch = (tenderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, status }: { matchId: string; status: ZERepeatStatus }) =>
      api.reviewRepeatMatch(tenderId, matchId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zt", "repeat-matches", tenderId] });
      qc.invalidateQueries({ queryKey: qk.tenders });
    },
  });
};

export const useCarryOverRepeatMatch = (tenderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, ...payload }: {
      matchId: string;
      route?: boolean;
      pricing?: boolean;
      variant?: string;
    }) => api.carryOverRepeatMatch(tenderId, matchId, payload),
    onSuccess: () => {
      // The copy creates a variant and (optionally) a pricing proposal on THIS tender —
      // both lists have to re-read, and the banner switches to "już zaciągnięto".
      qc.invalidateQueries({ queryKey: qk.variants(tenderId) });
      qc.invalidateQueries({ queryKey: ["zt", "repeat-matches", tenderId] });
      qc.invalidateQueries({ queryKey: ["tender", tenderId] });
    },
  });
};

export const useDetectRepeats = (tenderId: string) =>
  useMutation({ mutationFn: () => api.detectRepeats(tenderId) });
